import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Modal from '../../components/shared/Modal';
import { useConfirm } from '../../components/shared/Confirm';
import { money, hoy, fmtFecha } from '../../utils/finanzas';

/**
 * Caja menor de la plataforma.
 *
 * Modelo de base fija: la caja se abre con un monto y se repone hasta volver a
 * él. El saldo son las entradas menos las salidas, así que tras cada reposición
 * coincide otra vez con la base.
 *
 * Dos cosas que esta pantalla trata como de primera clase, porque son las que
 * de verdad duelen en una caja menor:
 *   - el soporte de cada gasto, sin el cual no se puede legalizar al reponer;
 *   - el arqueo, que es lo único que detecta un faltante.
 */

const VERDE = '#16a34a';
const AMBAR = '#d97706';
const ROJO  = '#dc2626';

const GASTO_VACIO = {
  concepto: '', monto: '', fecha: hoy(), categoria_id: '',
  responsable: '', soporte: '', notas: '',
};

// Lo que el servidor acepta como soporte adjunto: la foto del recibo o el PDF
// de la factura. Se valida tambien aqui para no hacer subir 8 MB en vano.
const TIPOS_SOPORTE = [
  'image/jpeg', 'image/pjpeg', 'image/png', 'image/webp',
  'image/heic', 'image/heif', 'application/pdf',
];
const ACCEPT_SOPORTE = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

const esPdf = (tipo) => String(tipo || '').includes('pdf');

const pesoLegible = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return '';
  return n < 1024 * 1024
    ? `${Math.max(Math.round(n / 1024), 1)} KB`
    : `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const TIPOS = {
  apertura:   { label: 'Apertura',   color: 'var(--color-accent)' },
  gasto:      { label: 'Gasto',      color: ROJO },
  reposicion: { label: 'Reposición', color: VERDE },
};

export default function CajaMenor({ categorias, esMovil, notificar, onGuardado }) {
  const [datos, setDatos]       = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo]       = useState('');
  const [guardando, setGuardando] = useState(false);

  const [formGasto, setFormGasto] = useState(GASTO_VACIO);
  const [editando, setEditando]   = useState(null);
  const [modal, setModal]         = useState(null); // gasto | config | arqueo
  const [config, setConfig]       = useState({ base: '', umbral_pct: 20, responsable: '' });
  const [arqueo, setArqueo]       = useState({ efectivo_contado: '', observacion: '' });

  // Soporte adjunto: el archivo nuevo por subir, el que ya tenia el gasto que
  // se esta editando, y la bandera de "quitalo y no pongas otro".
  const [archivo, setArchivo]             = useState(null);
  const [soporteActual, setSoporteActual] = useState(null);
  const [quitarArchivo, setQuitarArchivo] = useState(false);
  const [verSoporte, setVerSoporte]       = useState(null);
  const [abriendo, setAbriendo]           = useState(null);

  const confirmar = useConfirm();
  const cats = (categorias || []).filter(c => c.tipo === 'gasto');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get('/finanzas/caja');
      setDatos(r.data);
      setFallo('');
      return r.data;
    } catch (err) {
      const motivo = err.response?.status === 404
        ? 'El servidor todavía no tiene este módulo: falta subir routes/finanzas.js y reiniciar.'
        : (err.response?.data?.error || 'No se pudo cargar la caja menor');
      setFallo(motivo);
      setDatos(null);
      return null;
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ── acciones ──────────────────────────────────────────────────────────────
  const abrirGasto = (m) => {
    setArchivo(null);
    setQuitarArchivo(false);
    if (m) {
      setEditando(m.id);
      setFormGasto({
        concepto: m.concepto || '', monto: String(m.monto), fecha: m.fecha,
        categoria_id: m.categoria_id ? String(m.categoria_id) : '',
        responsable: m.responsable || '', soporte: m.soporte || '', notas: m.notas || '',
      });
      setSoporteActual(m.soporte_archivo
        ? { id: m.id, nombre: m.soporte_nombre, tipo: m.soporte_tipo, tamano: m.soporte_tamano }
        : null);
    } else {
      setEditando(null);
      setFormGasto({ ...GASTO_VACIO, responsable: datos?.caja?.responsable || '' });
      setSoporteActual(null);
    }
    setModal('gasto');
  };

  /** Toma el archivo del input y lo rechaza aqui mismo si no sirve. */
  const elegirArchivo = (file) => {
    if (!file) return;
    const maxMb = datos?.soporte_max_mb || 8;
    if (!TIPOS_SOPORTE.includes(file.type)) {
      return notificar('El soporte debe ser una imagen (JPG, PNG, WEBP, HEIC) o un PDF', true);
    }
    if (file.size > maxMb * 1024 * 1024) {
      return notificar(`El soporte no puede pesar más de ${maxMb} MB`, true);
    }
    setArchivo(file);
    setQuitarArchivo(false);
  };

  /**
   * Descarga el soporte y lo muestra. Se pide por la API y no por una URL
   * directa porque la carpeta no es publica: el archivo viaja con el token.
   */
  const abrirSoporte = async (m) => {
    setAbriendo(m.id);
    try {
      const r = await api.get(`/finanzas/caja/gastos/${m.id}/soporte`, { responseType: 'blob' });
      setVerSoporte({
        url: URL.createObjectURL(r.data),
        tipo: r.data.type || m.soporte_tipo || '',
        nombre: m.soporte_nombre || 'soporte',
        concepto: m.concepto,
      });
    } catch (err) {
      // Con responseType blob el mensaje de error tambien llega como blob.
      let motivo = 'No se pudo abrir el soporte';
      try { motivo = JSON.parse(await err.response.data.text()).error || motivo; } catch { /* se queda el generico */ }
      notificar(motivo, true);
    } finally { setAbriendo(null); }
  };

  const cerrarSoporte = () => {
    if (verSoporte?.url) URL.revokeObjectURL(verSoporte.url);
    setVerSoporte(null);
  };

  const guardarGasto = async () => {
    if (!formGasto.concepto.trim()) return notificar('Escribe el concepto del gasto', true);
    if (!Number(formGasto.monto))   return notificar('El valor debe ser mayor que cero', true);
    setGuardando(true);
    try {
      const cuerpo = { ...formGasto, monto: Number(formGasto.monto) };

      // Con adjunto el gasto viaja como multipart; sin el, como el JSON de
      // siempre. Asi no se paga el precio del multipart en cada gasto suelto.
      let envio = cuerpo;
      if (archivo || quitarArchivo) {
        const fd = new FormData();
        Object.entries(cuerpo).forEach(([k, v]) => fd.append(k, v ?? ''));
        if (archivo) fd.append('soporte_archivo', archivo);
        else if (quitarArchivo) fd.append('quitar_soporte', '1');
        envio = fd;
      }

      if (editando) await api.put(`/finanzas/caja/gastos/${editando}`, envio);
      else          await api.post('/finanzas/caja/gastos', envio);
      notificar(editando ? 'Gasto actualizado' : 'Gasto registrado');
      setModal(null); cargar(); onGuardado();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo guardar', true);
    } finally { setGuardando(false); }
  };

  const eliminarGasto = async (m) => {
    if (!await confirmar({
      titulo: 'Eliminar gasto de caja',
      mensaje: `¿Eliminar "${m.concepto}"?`,
      detalle: 'El saldo de la caja volverá a subir en ese valor.',
      aceptar: 'Eliminar', peligro: true,
    })) return;
    try {
      await api.delete(`/finanzas/caja/gastos/${m.id}`);
      notificar('Gasto eliminado');
      cargar(); onGuardado();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo eliminar', true);
    }
  };

  const guardarConfig = async () => {
    if (!Number(config.base)) return notificar('La base debe ser mayor que cero', true);
    setGuardando(true);
    try {
      await api.put('/finanzas/caja', { ...config, base: Number(config.base) });
      notificar('Caja actualizada');
      setModal(null); cargar();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo guardar', true);
    } finally { setGuardando(false); }
  };

  const reponer = async () => {
    const falta = datos.n_sin_soporte;
    if (!await confirmar({
      titulo: 'Reponer caja menor',
      mensaje: `Se repondrán ${money(datos.a_reponer)} para volver a la base de ${money(datos.caja.base)}.`,
      detalle: falta
        ? `Ojo: ${falta} gasto(s) por ${money(datos.sin_soporte)} no tienen soporte y quedarán marcados como no legalizados. El total se registrará como un gasto en la pestaña Gastos.`
        : 'El total se registrará como un gasto en la pestaña Gastos.',
      aceptar: 'Reponer', peligro: !!falta,
    })) return;
    try {
      const r = await api.post('/finanzas/caja/reponer');
      notificar(r.data?.message || 'Caja repuesta');
      cargar(); onGuardado();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo reponer', true);
    }
  };

  const guardarArqueo = async () => {
    if (arqueo.efectivo_contado === '') return notificar('Escribe el efectivo contado', true);
    setGuardando(true);
    try {
      const r = await api.post('/finanzas/caja/arqueos', {
        efectivo_contado: Number(arqueo.efectivo_contado),
        observacion: arqueo.observacion,
      });
      notificar(r.data?.message || 'Arqueo registrado', r.data?.diferencia !== 0);
      setModal(null); setArqueo({ efectivo_contado: '', observacion: '' }); cargar();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo registrar el arqueo', true);
    } finally { setGuardando(false); }
  };

  // ── estados de carga ──────────────────────────────────────────────────────
  if (cargando && !datos) {
    return <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando caja menor...</div>;
  }
  if (fallo) {
    return (
      <div className="card" style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>⚠️</div>
        <div style={{ color: 'var(--color-error)', fontWeight: 600, marginBottom: 6 }}>No se pudo cargar</div>
        <p style={{ margin: '0 auto', maxWidth: '52ch', fontSize: '0.84rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{fallo}</p>
        <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const { caja, saldo, porcentaje, a_reponer: aReponer, salidas, sin_soporte: sinSoporte,
          n_sin_soporte: nSinSoporte, movimientos, por_categoria: porCat, arqueos } = datos;

  // Caja sin configurar: no tiene sentido mostrar un libro vacío.
  if (!caja.configurada) {
    return (
      <>
        <div className="card" style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: 10 }}>💵</div>
          <h3 style={{ margin: '0 0 8px' }}>Configura la caja menor</h3>
          <p style={{ margin: '0 auto 16px', maxWidth: '54ch', fontSize: '0.86rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
            Define el monto base con el que se abre la caja y quién responde por ella. Los gastos
            se van descontando de ahí, y cuando el saldo baje podrás reponerla para volver a la base.
          </p>
          <button className="btn btn-primary" onClick={() => {
            setConfig({ base: '', umbral_pct: 20, responsable: '' });
            setModal('config');
          }}>Abrir caja menor</button>
        </div>
        {modal === 'config' && <ModalConfig {...{ config, setConfig, guardando, guardarConfig, setModal }} />}
      </>
    );
  }

  const bajo = porcentaje != null && porcentaje <= caja.umbral_pct;
  const ultimo = arqueos[0];

  return (
    <div>
      {/* ── estado de la caja ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? '1fr' : '1.5fr 1fr',
        }}>
          <div style={{ padding: '1.25rem 1.4rem' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-text-faint)' }}>
              Disponible en caja
            </div>
            <div style={{ fontSize: '2.3rem', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05, color: bajo ? AMBAR : 'var(--color-text)' }}>
              {money(saldo)}
            </div>

            <div style={{ height: 9, background: 'var(--color-surface-3)', borderRadius: 99, overflow: 'hidden', margin: '14px 0 8px', position: 'relative' }}>
              <div style={{ width: `${Math.min(Math.max(porcentaje || 0, 0), 100)}%`, height: '100%', background: bajo ? AMBAR : VERDE, borderRadius: 99, transition: 'width .4s ease' }} />
              <span style={{ position: 'absolute', top: -4, bottom: -4, left: `${caja.umbral_pct}%`, width: 2, background: 'var(--color-text-faint)', opacity: 0.7 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              <span>{porcentaje}% de la base</span>
              <span>Umbral de reposición: {caja.umbral_pct}%</span>
            </div>

            {bajo && (
              <div className="alert alert-warning" style={{ marginTop: 14 }}>
                <span>⚠</span>
                <div>
                  <strong>Queda menos del {caja.umbral_pct}% de la base.</strong> Repón la caja para
                  volver a {money(caja.base)}. El total gastado se registrará como un gasto en <strong>Gastos</strong>.
                </div>
              </div>
            )}
          </div>

          <div style={{
            padding: '1.25rem 1.4rem', background: 'var(--color-surface-2)',
            borderLeft: esMovil ? 0 : '1px solid var(--color-border)',
            borderTop: esMovil ? '1px solid var(--color-border)' : 0,
          }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-text-faint)', marginBottom: 8 }}>
              Estado
            </div>
            <Dato k="Base asignada" v={money(caja.base)} />
            <Dato k="Gastado" v={money(salidas)} />
            <Dato k="Por reponer" v={money(aReponer)} />
            <Dato k="Responsable" v={caja.responsable || '—'} />
            <Dato k="Abierta desde" v={fmtFecha(caja.abierta_en)} />
            <Dato k="Último arqueo" v={ultimo
              ? `${fmtFecha(ultimo.fecha)} · ${ultimo.diferencia === 0 ? 'sin diferencia' : money(ultimo.diferencia)}`
              : 'Nunca'} />
          </div>
        </div>
      </div>

      {nSinSoporte > 0 && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          <span>🧾</span>
          <div>
            <strong>{nSinSoporte} gasto(s) por {money(sinSoporte)} no tienen soporte.</strong> Sin
            factura o recibo no se pueden legalizar en la reposición. Antes de reponer, edítalos y
            agrega el número del soporte o adjunta la foto del recibo o el PDF de la factura.
          </div>
        </div>
      )}

      {/* ── acciones ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button className="btn btn-primary" onClick={() => abrirGasto(null)}>+ Registrar gasto</button>
        <button className="btn btn-secondary" onClick={reponer} disabled={aReponer <= 0}>↻ Reponer caja</button>
        <button className="btn btn-secondary" onClick={() => { setArqueo({ efectivo_contado: '', observacion: '' }); setModal('arqueo'); }}>⚖ Hacer arqueo</button>
        <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => {
          setConfig({ base: String(caja.base), umbral_pct: caja.umbral_pct, responsable: caja.responsable || '' });
          setModal('config');
        }}>⚙ Configurar</button>
      </div>

      {/* ── movimientos ── */}
      <div className="card" style={{ padding: 0, minWidth: 0 }}>
        {movimientos.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Aún no hay movimientos en la caja
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', minWidth: esMovil ? 640 : 860 }}>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  {!esMovil && <th>Categoría</th>}
                  {!esMovil && <th>Responsable</th>}
                  <th>Soporte</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                  <th className="col-acciones"></th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map(m => {
                  const t = TIPOS[m.tipo] || TIPOS.gasto;
                  const esGasto = m.tipo === 'gasto';
                  return (
                    <tr key={m.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{fmtFecha(m.fecha)}</td>
                      <td style={{ fontSize: '0.85rem' }}>
                        <div style={{ fontWeight: 500 }}>{m.concepto}</div>
                        {!esGasto && (
                          <div style={{ fontSize: '0.72rem', color: t.color, fontWeight: 600 }}>{t.label}</div>
                        )}
                        {m.notas && <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)' }}>{m.notas}</div>}
                      </td>
                      {!esMovil && (
                        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{m.categoria || '—'}</td>
                      )}
                      {!esMovil && (
                        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{m.responsable || '—'}</td>
                      )}
                      <td>
                        {!esGasto ? <span style={{ color: 'var(--color-text-faint)' }}>—</span> : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            {m.soporte && (
                              <span className="badge badge-active" style={{ fontSize: '0.68rem' }}>✓ {m.soporte}</span>
                            )}
                            {m.soporte_archivo && (
                              <button
                                className="btn btn-secondary btn-icon"
                                style={{ fontSize: '0.75rem', padding: '2px 7px' }}
                                disabled={abriendo === m.id}
                                title={`Ver ${esPdf(m.soporte_tipo) ? 'PDF' : 'imagen'}: ${m.soporte_nombre || 'soporte'}`}
                                onClick={() => abrirSoporte(m)}
                              >
                                {abriendo === m.id ? '…' : (esPdf(m.soporte_tipo) ? '📄' : '🖼')}
                              </button>
                            )}
                            {!m.soporte && !m.soporte_archivo && (
                              <span className="badge badge-suspended" style={{ fontSize: '0.68rem' }}>Sin soporte</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: esGasto ? ROJO : VERDE }}>
                        {esGasto ? '−' : '+'}{money(m.monto)}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--color-text-muted)' }}>
                        {money(m.saldo)}
                      </td>
                      <td className="col-acciones">
                        {esGasto && (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button className="btn btn-secondary btn-icon" title="Editar" onClick={() => abrirGasto(m)}>✎</button>
                            <button className="btn btn-ghost btn-icon" title="Eliminar" onClick={() => eliminarGasto(m)}>🗑</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── arqueo y reparto ── */}
      <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem', gridTemplateColumns: esMovil ? '1fr' : '1fr 1fr' }}>
        <div className="card" style={{ padding: '1rem 1.15rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Arqueo de caja</h3>
          <p style={{ margin: '4px 0 12px', fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            Contar el efectivo y compararlo con el saldo en libros. Es lo único que detecta un faltante.
          </p>
          <Dato k="Saldo en libros" v={money(saldo)} />
          {arqueos.slice(0, 3).map(a => (
            <Dato key={a.id} k={fmtFecha(a.fecha)} v={
              <span style={{ color: a.diferencia === 0 ? VERDE : ROJO }}>
                {a.diferencia === 0 ? 'Sin diferencia' : `${a.diferencia > 0 ? 'Sobra ' : 'Falta '}${money(Math.abs(a.diferencia))}`}
              </span>
            } />
          ))}
          {!arqueos.length && (
            <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              Todavía no se ha hecho ningún arqueo.
            </p>
          )}
        </div>

        <div className="card" style={{ padding: '1rem 1.15rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem' }}>En qué se fue</h3>
          <p style={{ margin: '4px 0 12px', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
            Sobre {money(salidas)} gastados desde la apertura.
          </p>
          {porCat.length === 0
            ? <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Sin gastos todavía.</p>
            : porCat.map(c => <Dato key={c.categoria} k={c.categoria} v={money(c.total)} />)}
        </div>
      </div>

      {/* ── modales ── */}
      {modal === 'gasto' && (
        <Modal titulo={`${editando ? 'Editar' : 'Registrar'} gasto de caja`} ancho={470} onCerrar={() => setModal(null)}
          pie={<>
            <button className="btn btn-ghost" onClick={() => setModal(null)} disabled={guardando}>Cancelar</button>
            <button className="btn btn-primary" onClick={guardarGasto} disabled={guardando}>
              {guardando ? 'Guardando...' : 'Guardar gasto'}
            </button>
          </>}>
          <div>
            <div className="form-group">
              <label className="form-label">Concepto</label>
              <input className="form-input" placeholder="Ej. Sobres y mensajería"
                     value={formGasto.concepto}
                     onChange={e => setFormGasto(f => ({ ...f, concepto: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Valor</label>
                <input type="number" min="0" step="1" className="form-input" placeholder="0"
                       value={formGasto.monto}
                       onChange={e => setFormGasto(f => ({ ...f, monto: e.target.value }))} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Fecha</label>
                <input type="date" className="form-input" value={formGasto.fecha}
                       onChange={e => setFormGasto(f => ({ ...f, fecha: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Categoría</label>
                <select className="form-select" value={formGasto.categoria_id}
                        onChange={e => setFormGasto(f => ({ ...f, categoria_id: e.target.value }))}>
                  <option value="">Sin categoría</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Responsable</label>
                <input className="form-input" placeholder="Quién lo pagó"
                       value={formGasto.responsable}
                       onChange={e => setFormGasto(f => ({ ...f, responsable: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">N° de soporte</label>
              <input className="form-input" placeholder="N° de factura o recibo"
                     value={formGasto.soporte}
                     onChange={e => setFormGasto(f => ({ ...f, soporte: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Soporte adjunto</label>
              <CampoSoporte
                archivo={archivo}
                actual={quitarArchivo ? null : soporteActual}
                maxMb={datos?.soporte_max_mb || 8}
                onElegir={elegirArchivo}
                onVer={() => soporteActual && abrirSoporte({
                  id: soporteActual.id,
                  concepto: formGasto.concepto,
                  soporte_nombre: soporteActual.nombre,
                  soporte_tipo: soporteActual.tipo,
                })}
                onQuitar={() => {
                  if (archivo) setArchivo(null);
                  else setQuitarArchivo(true);
                }}
              />
              <p style={{ margin: '6px 2px 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                Sin soporte —número o archivo— el gasto no se puede legalizar en la reposición.
              </p>
            </div>
            {Number(formGasto.monto) > 0 && !editando && (
              <div style={{
                background: 'var(--color-surface-3)', borderRadius: 8, padding: '10px 12px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '0.84rem',
              }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Saldo después de este gasto</span>
                <strong style={{ color: (saldo - Number(formGasto.monto)) < 0 ? ROJO : AMBAR }}>
                  {money(saldo - Number(formGasto.monto))}
                </strong>
              </div>
            )}
          </div>
        </Modal>
      )}

      {verSoporte && (
        <Modal titulo={`Soporte · ${verSoporte.concepto || ''}`} ancho={760} onCerrar={cerrarSoporte}
          pie={<>
            <a className="btn btn-secondary" href={verSoporte.url} download={verSoporte.nombre}>⬇ Descargar</a>
            <a className="btn btn-secondary" href={verSoporte.url} target="_blank" rel="noopener noreferrer">↗ Abrir aparte</a>
            <button className="btn btn-primary" onClick={cerrarSoporte}>Cerrar</button>
          </>}>
          {esPdf(verSoporte.tipo) ? (
            <iframe
              title="Soporte en PDF"
              src={verSoporte.url}
              style={{ width: '100%', height: '70vh', border: '1px solid var(--color-border)', borderRadius: 8 }}
            />
          ) : (
            <img
              src={verSoporte.url}
              alt={`Soporte de ${verSoporte.concepto || 'gasto'}`}
              style={{ display: 'block', maxWidth: '100%', maxHeight: '70vh', margin: '0 auto', borderRadius: 8 }}
            />
          )}
        </Modal>
      )}

      {modal === 'config' && <ModalConfig {...{ config, setConfig, guardando, guardarConfig, setModal }} />}

      {modal === 'arqueo' && (
        <Modal titulo="Arqueo de caja" ancho={430} onCerrar={() => setModal(null)}
          pie={<>
            <button className="btn btn-ghost" onClick={() => setModal(null)} disabled={guardando}>Cancelar</button>
            <button className="btn btn-primary" onClick={guardarArqueo} disabled={guardando}>
              {guardando ? 'Guardando...' : 'Registrar arqueo'}
            </button>
          </>}>
          <div>
            <div style={{
              background: 'var(--color-surface-3)', borderRadius: 8, padding: '10px 12px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, fontSize: '0.86rem',
            }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Saldo en libros</span>
              <strong>{money(saldo)}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">Efectivo contado</label>
              <input type="number" min="0" step="1" className="form-input" placeholder="0"
                     value={arqueo.efectivo_contado}
                     onChange={e => setArqueo(a => ({ ...a, efectivo_contado: e.target.value }))} />
            </div>
            {arqueo.efectivo_contado !== '' && (
              <div style={{ marginBottom: 14, fontSize: '0.86rem' }}>
                {(() => {
                  const dif = Number(arqueo.efectivo_contado) - saldo;
                  if (dif === 0) return <span style={{ color: VERDE }}>✓ Cuadra exacto con el saldo en libros.</span>;
                  return (
                    <span style={{ color: ROJO }}>
                      {dif > 0 ? 'Sobran ' : 'Faltan '}<strong>{money(Math.abs(dif))}</strong>. Explica la diferencia abajo.
                    </span>
                  );
                })()}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Observación</label>
              <textarea className="form-input" rows={2} placeholder="Qué explica la diferencia, si la hay"
                        value={arqueo.observacion}
                        onChange={e => setArqueo(a => ({ ...a, observacion: e.target.value }))} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Selector del archivo de soporte.
 *
 * Tres estados: un archivo recien elegido (con vista previa si es imagen), el
 * que ya tenia el gasto (se puede ver o quitar) o ninguno.
 */
function CampoSoporte({ archivo, actual, maxMb, onElegir, onVer, onQuitar }) {
  const [vistaPrevia, setVistaPrevia] = useState(null);

  useEffect(() => {
    if (!archivo || esPdf(archivo.type)) { setVistaPrevia(null); return undefined; }
    const url = URL.createObjectURL(archivo);
    setVistaPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [archivo]);

  const caja = {
    display: 'flex', alignItems: 'center', gap: 10,
    border: '1px solid var(--color-border)', borderRadius: 8, padding: '9px 11px',
    background: 'var(--color-surface-2)',
  };

  if (archivo || actual) {
    const nombre = archivo ? archivo.name : (actual.nombre || 'soporte');
    const tipo   = archivo ? archivo.type : actual.tipo;
    const peso   = pesoLegible(archivo ? archivo.size : actual.tamano);
    return (
      <div style={caja}>
        {vistaPrevia ? (
          <img src={vistaPrevia} alt="Vista previa del soporte"
               style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
        ) : (
          <span style={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0 }}>{esPdf(tipo) ? '📄' : '🖼'}</span>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {nombre}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
            {archivo ? `Se subirá al guardar${peso ? ` · ${peso}` : ''}` : `Adjunto${peso ? ` · ${peso}` : ''}`}
          </div>
        </div>
        {!archivo && (
          <button type="button" className="btn btn-secondary btn-icon" title="Ver soporte" onClick={onVer}>👁</button>
        )}
        <button type="button" className="btn btn-ghost btn-icon" title="Quitar soporte" onClick={onQuitar}>✕</button>
      </div>
    );
  }

  return (
    <label style={{ ...caja, cursor: 'pointer', borderStyle: 'dashed' }}>
      <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>📎</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 500 }}>Adjuntar soporte</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
          Foto del recibo o PDF de la factura · hasta {maxMb} MB
        </div>
      </div>
      <input type="file" accept={ACCEPT_SOPORTE} style={{ display: 'none' }}
             onChange={e => { onElegir(e.target.files?.[0]); e.target.value = ''; }} />
    </label>
  );
}

function Dato({ k, v }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 12,
      padding: '7px 0', borderBottom: '1px solid var(--color-border)', fontSize: '0.84rem',
    }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{k}</span>
      <strong style={{ fontWeight: 600, textAlign: 'right' }}>{v}</strong>
    </div>
  );
}

function ModalConfig({ config, setConfig, guardando, guardarConfig, setModal }) {
  return (
    <Modal titulo="Configurar caja menor" ancho={430} onCerrar={() => setModal(null)}
      pie={<>
        <button className="btn btn-ghost" onClick={() => setModal(null)} disabled={guardando}>Cancelar</button>
        <button className="btn btn-primary" onClick={guardarConfig} disabled={guardando}>
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
      </>}>
      <div>
        <div className="form-group">
          <label className="form-label">Base de la caja</label>
          <input type="number" min="0" step="1000" className="form-input" placeholder="500000"
                 value={config.base}
                 onChange={e => setConfig(c => ({ ...c, base: e.target.value }))} />
          <p style={{ margin: '5px 2px 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
            El monto con el que se abre y al que vuelve en cada reposición.
          </p>
        </div>
        <div className="form-group">
          <label className="form-label">Avisar cuando quede menos del</label>
          <select className="form-select" value={config.umbral_pct}
                  onChange={e => setConfig(c => ({ ...c, umbral_pct: Number(e.target.value) }))}>
            {[10, 15, 20, 25, 30, 40, 50].map(p => <option key={p} value={p}>{p}% de la base</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Responsable</label>
          <input className="form-input" placeholder="Quién responde por el efectivo"
                 value={config.responsable}
                 onChange={e => setConfig(c => ({ ...c, responsable: e.target.value }))} />
        </div>
      </div>
    </Modal>
  );
}
